// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import { quantityInputStyle, QuantityInputStyles } from "@flip/App/SharedStyle/QuantityInputStyle";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";

import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import updateMaintenanceInfo from "@insite/client-framework/Store/Pages/BudgetManagement/Handlers/UpdateMaintenanceInfo";
import translate from "@insite/client-framework/Translate";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import { Clickable, Icon } from "@insite/mobius/index";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import cloneDeep from "lodash/cloneDeep";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends TextFieldProps {
    labelOverride?: React.ReactNode;
    budgetAmount: number | undefined;
    index: number;
    extendedStyles?: QuantityInputStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        maintenanceInfo: state.pages.budgetManagement.maintenanceInfo,
    };
};

const mapDispatchToProps = {
    updateMaintenanceInfo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const AssignBudgetStepper = ({
    labelOverride,
    index,
    extendedStyles,
    maintenanceInfo,
    budgetAmount,
    updateMaintenanceInfo,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(quantityInputStyle, extendedStyles));
    const [budgetPeriod, setBudgetPeriod] = useState(budgetAmount);

    useEffect(() => {
        if (budgetAmount === Number(budgetPeriod)) {
            return;
        }
        setBudgetPeriod(budgetAmount);
    }, [budgetAmount]);

    const updateBudgetAmountAction = (index: number, type: "increase" | "decrease") => {
        const event = { target: { value: `${Number(budgetPeriod) + (type === "increase" ? 1 : -1)}` } } as any;
        handleBudgetAmountChange(index, event);
    };

    const handleBudgetAmountChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value === "" ? undefined : Number(event.target.value);
        if (maintenanceInfo) {
            const currentBudget = cloneDeep(maintenanceInfo);
            currentBudget.budgetLineCollection![index].currentFiscalYearBudget = value;
            setBudgetPeriod(value);
            updateMaintenanceInfo({ value: currentBudget });
        }
    };
    const handleBudgetOnBlur = () => {
        if (maintenanceInfo) {
            const currentBudget = cloneDeep(maintenanceInfo);
            currentBudget.budgetLineCollection?.forEach(budgetLine => {
                if (!budgetLine.currentFiscalYearBudget) {
                    budgetLine.currentFiscalYearBudget = 0;
                }
            });
            updateMaintenanceInfo({ value: currentBudget });
        }
    };
    return (
        <StyledWrapper {...styles?.wrapper}>
            <Clickable
                {...styles?.actionClickable}
                disabled={Number(budgetPeriod) <= 0}
                onClick={() => updateBudgetAmountAction(index, "decrease")}
                data-test-selector="custom_budget_amount_decrease"
                aria-label="decrease budget amount"
            >
                <Icon src={Minus} size={12} />
            </Clickable>
            {/* <VisuallyHidden as="label" htmlFor={textFieldId}>
                {translate("QTY")}
            </VisuallyHidden> */}
            <TextField
                type="qty"
                value={budgetPeriod || 0}
                data-test-selector={`budgetAmountField_${index}`}
                onChange={event => handleBudgetAmountChange(index, event)}
                onBlur={handleBudgetOnBlur}
                {...styles?.quantityOrderedTextField}
                {...otherProps}
            />
            <Clickable
                {...styles?.actionClickable}
                disabled={Number(budgetPeriod) >= 999999}
                onClick={() => updateBudgetAmountAction(index, "increase")}
                data-test-selector="custom_budget_amount_increase"
                aria-label="increase budget amount"
            >
                <Icon src={Plus} size={12} />
            </Clickable>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(AssignBudgetStepper);
